// authStorage.ts

import secureStorage from '@shared/services/secureStorage';
import { storageService } from '@shared/services/storageService';
import { agencyStorage } from './agencyStorage';
import { timeZoneStorage } from './timeZoneStorage';
import { SHOP_STORAGE_KEY } from '@shared/store/shopSlice';
import { TECHNICIAN_STORAGE_KEY, CREATED_USER_ID_KEY } from '@shared/store/technicianSlice';
import { MENU_ACTIVE_TAB_KEY } from '@shared/store/menuSlice';
import { REPAIR_ID_KEY, SHOW_REPAIR_HISTORY_KEY } from '@shared/store/repairSlice';
import { TIMEZONE_STORAGE_KEY } from '@shared/store/userSlice';
import { AUTH_USER_ID_KEY } from './userIdStorage';

// Keep in sync with AppNavigator.tsx
const NAV_SCREEN_KEY = 'LAST_NAV_SCREEN';

export const authStorage = {
  async getAccessToken(): Promise<string | null> {
    try {
      return await storageService.getAuthToken();
    } catch (error) {
      console.warn('Failed to read access token', error);
      return null;
    }
  },

  async setAccessToken(token: string): Promise<void> {
    try {
      await storageService.setAuthToken(token);
    } catch (error) {
      console.warn('Failed to store access token', error);
    }
  },

  async getRefreshToken(): Promise<string | null> {
    try {
      return await storageService.getRefreshToken();
    } catch (error) {
      console.warn('Failed to read refresh token', error);
      return null;
    }
  },

  async setRefreshToken(token: string): Promise<void> {
    try {
      await storageService.setRefreshToken(token);
    } catch (error) {
      console.warn('Failed to store refresh token', error);
    }
  },

  async getLoginHint(): Promise<string | null> {
    try {
      return await storageService.getLoginHint();
    } catch (error) {
      console.warn('Failed to read login hint', error);
      return null;
    }
  },

  async setLoginHint(hint: string): Promise<void> {
    try {
      await storageService.setLoginHint(hint);
    } catch (error) {
      console.warn('Failed to store login hint', error);
    }
  },

  async setForceReauth(): Promise<void> {
    try {
      await storageService.setForceReauth();
    } catch (error) {
      console.warn('Failed to set force reauth flag', error);
    }
  },

  async getForceReauth(): Promise<boolean> {
    try {
      return await storageService.getForceReauth();
    } catch (error) {
      console.warn('Failed to get force reauth flag', error);
      return false;
    }
  },

  async clearForceReauth(): Promise<void> {
    try {
      await storageService.clearForceReauth();
    } catch (error) {
      console.warn('Failed to clear force reauth flag', error);
    }
  },

  async clear(): Promise<void> {
    try {
      // agencyStorage (in-memory) is cleared below but the PERSISTED agency
      // code (storageService / secureStorage) is intentionally NOT removed.
      // The agency code is tenant configuration, not user-session data — it
      // must survive logout so that bootstrap() can rehydrate agencyStorage
      // on the next page load and the X-Agency-Id header is present on the
      // very first BFF call (/api/auth/validate) of the second login attempt.
      // Removing it here caused a 403 on every second login.
      agencyStorage.clear();
      timeZoneStorage.clear();
      await Promise.all([
        storageService.removeAuthToken(),
        storageService.removeRefreshToken(),
        // Explicitly clear loginHint on logout so the user is not forced
        // into the same entra account on next sign-in.
        storageService.removeLoginHint(),
        storageService.setForceReauth(),
        // Clear last visited screen so next cold start begins at AgencySelectionScreen
        secureStorage.removeItem(NAV_SCREEN_KEY),
        // Clear native-persisted Redux state so stale data isn't restored after logout
        secureStorage.removeItem(SHOP_STORAGE_KEY),
        secureStorage.removeItem(TECHNICIAN_STORAGE_KEY),
        secureStorage.removeItem(CREATED_USER_ID_KEY),
        secureStorage.removeItem(MENU_ACTIVE_TAB_KEY),
        secureStorage.removeItem(REPAIR_ID_KEY),
        secureStorage.removeItem(SHOW_REPAIR_HISTORY_KEY),
        secureStorage.removeItem('OPEN_VOICE_MODAL_ON_RETURN'),
        secureStorage.removeItem(TIMEZONE_STORAGE_KEY),
        secureStorage.removeItem(AUTH_USER_ID_KEY),
      ]);
    } catch (error) {
      console.warn('Failed to clear auth tokens', error);
    }
  },
};
